/**
	We are using the library Valtio to create global state proxies -> https://github.com/pmndrs/valtio
	Some organization patterns -> https://github.com/pmndrs/valtio/wiki/How-to-organize-actions

	A GlobalState class that is instantiated by passing an initial state proxy. It provides methods to get, mutate and subscribe to the state.

	Reasons:
	- Can access state both from inside react components and outside in vanilla js (good for Pioneer callbacks)
	- Lightweight (2.3kB gzipped)
	- Better rendering efficiency with targetted state subscription capabilities

	Notes:
	- From within react components, we can use the useSnapshot hooks. Look inside events_overlay for an example.
	- From outside react components, it's slightly more imperative, we need to explicitly set the first state using a state snapshot,
	then subscribe to the state and attach a callback for when it changes. See the scene_manger for an example.

	Future work:
	Using the 'ref' method, it's possible to hold global references large nested object without tracking them.
	We are already storing the 'get' method to access managers, perhaps _scene and _pioneer would also be useful.
	It would prevent drilling them everywhere as arguments.
*/

import { proxy, subscribe, snapshot } from 'valtio/vanilla';
import { subscribeKey } from 'valtio/utils';

//merge questions lots of event based items removed from this list 
import {
	LIGHT_STATUS,
	CITY_NAME_STATUS,
	GROUND_TRACK_STATUS,
	EVENTS_SHOW_ON_GLOBE,
	POI_SHOW_ON_GLOBE,
	GMAP_SHOW_ON_GLOBE,
	EVENTS_YEAR_INDEX,
	POI_YEAR_INDEX,
	GMAP_YEAR_INDEX,
	EVENTS_CATEGORY_INDEX,
	POI_CATEGORY_INDEX,
	GMAP_CATEGORY_INDEX,
	EVENTS_SHOW_LATEST,
	POI_SHOW_LATEST,
	GMAP_SHOW_LATEST,
  MISSIONS_TIME_GROUP_INDEX,
	KIOSK,
  EVENT_CATEGORIES,
  POI_CATEGORIES,
  GMAP_CATEGORIES
} from '../config/constants';
import { isLanguageCelcius } from '../helpers/tools';

/**
 * @ignore
 * @typedef {import('valtio/vanilla').proxy} Proxy
 */


/**
 * Global state class.
 */
class GlobalState {
	/**
	 * Constructor.
	 * @param {Proxy} proxyState
	 */
	constructor(proxyState) {
		this._state = proxyState;
	}

	/**
	 * Subscribes to any state changes, calls callback passing state as arg.
	 * @param {function} callback
	 * @returns {function} unsubscribe function
	 */
	subscribeAll(callback) {
		return subscribe(this._state, () => callback(snapshot(this._state)));
	}

	/**
	 * Subscribe to a specific item (key) within this global state.
	 * When that item's value changes, pass it into, and call the callback
	 * @param {string} item
	 * @param {function} callback
	 * @returns {function} unsubscribe function
	 */
	subscribeTo(item, callback) {
		return this._state[item] !== undefined ? subscribeKey(this._state, item, callback) : false;
	}

	/**
	 * Sets the state.
	 * @param {object} name
	 */
	setGlobalState(stateObj) {
		Object.keys(stateObj).forEach(itemName => {
			if (this._state[itemName] !== undefined) {
				this._state[itemName] = stateObj[itemName];
			} else {
				console.error(`Cannot setGlobalState for ${itemName}. It does not exist.`);
			}
		});
	}

	/**
	 * Sets the state.
	 * @param {object} name
	 */
	/* setGlobalStatePoI(stateObj) {
		Object.keys(stateObj).forEach(itemName => {
			if (this._state[itemName] !== undefined) {
				this._state[itemName] = stateObj[itemName];
			} else {
				console.error(`Cannot setGlobalStatePoI for ${itemName}. It does not exist.`);
			}
		});
	}*/

	/**
	 * Returns snapshot of the current _state. Used outside react components.
	 */
	get stateSnapshot() {
		return snapshot(this._state);
	}

	/**
	 * Returns snapshot of the current _state. Used outside react components.
	 */
	/* get stateSnapshotPoI() {
		return snapshot(this._state);
	}*/

	/**
	 * Returns current _state. Used inside react components (in conjunction with useSnapshot hook)
	 */
	get state() {
		return this._state;
	}

	/**
	 * Returns current _state. Used inside react components (in conjunction with useSnapshot hook)
	 */
	/* get statePoI() {
		return this._state;
	}*/
}

/**
 * Define proxies with default states.
 */
const eventsProxy = proxy({
	events: {},
	showOnGlobe: EVENTS_SHOW_ON_GLOBE,
	showLatest: EVENTS_SHOW_LATEST,
	yearIndex: EVENTS_YEAR_INDEX,
  cats: EVENT_CATEGORIES,
	categoryIndex: EVENTS_CATEGORY_INDEX,
	missionTimeIndex: MISSIONS_TIME_GROUP_INDEX
});

const poiProxy = proxy({
	showOnGlobePoI: POI_SHOW_ON_GLOBE,
	showLatestPoI: POI_SHOW_LATEST,
	yearIndex: POI_YEAR_INDEX,
	categoryIndex: POI_CATEGORY_INDEX,
  poi_cats: POI_CATEGORIES,
  pois: {},
  poiYears: [],
	missionTimeIndex: MISSIONS_TIME_GROUP_INDEX
});

const gmapProxy = proxy({
	showOnGlobeGmap: GMAP_SHOW_ON_GLOBE,
	showLatestGmap: GMAP_SHOW_LATEST,
	yearIndex: GMAP_YEAR_INDEX,
	categoryIndex: GMAP_CATEGORY_INDEX,
  gmap_cats: GMAP_CATEGORIES,
  gmaps: {},
  gmapYears: [],
	missionTimeIndex: MISSIONS_TIME_GROUP_INDEX
});

const lightProxy = proxy({
	fill: LIGHT_STATUS
});

const layersProxy = proxy({
	blueMarble: false,
	cityNames: CITY_NAME_STATUS,
	groundTracks: GROUND_TRACK_STATUS
});

const refsProxy = proxy({
	get: null,
	pioneer: null
});

const timeProxy = proxy({
	isPlaying: null,
	rate: null,
	formattedRate: {}
});

const datasetProxy = proxy({
	currentVS: null,
	currentDataset: null,
	hasAnimation: null,
	isPaused: null,
	isLoading: null,
	animationIndex: null,
	animationDates: { start: null, end: null }
});

const datareadProxy = proxy({
	colorRectStyle: null,
	readoutData: null,
	isCelcius: isLanguageCelcius(),
	isColorBarShowing: null
});

const modalProxy = proxy({
	isVisible: false,
	startRange: null,
	endRange: null,
	numMissingDatasets: null,
	onClose: null
});

const spacecraftProxy = proxy({
	spacecraftName: null,
	isZoomedOut: null,
	showingAllSC: null
});

const compareProxy = proxy({
	compareObject: null
});

const videoProxy = proxy({
	isVideoPlaying: false,
	isVideoLoading: false,
	videoId: null,
	videoOpenedFrom: null
});

const kioskProxy = proxy({
	kioskModeOn: false,
	kioskUrl: false,
	playAutoplay: true,
	missionTimedOut: false
});

/**
 * Export instantiated GlobalState classes as stores.
 */
export const eventsStore = new GlobalState(eventsProxy);
export const poiStore = new GlobalState(poiProxy);
export const gmapStore = new GlobalState(gmapProxy);
export const lightStore = new GlobalState(lightProxy);
export const layersStore = new GlobalState(layersProxy);
export const refsStore = new GlobalState(refsProxy);
export const timeStore = new GlobalState(timeProxy);
export const datasetStore = new GlobalState(datasetProxy);
export const datareadStore = new GlobalState(datareadProxy);
export const modalStore = new GlobalState(modalProxy);
export const spacecraftStore = new GlobalState(spacecraftProxy);
export const compareStore = new GlobalState(compareProxy);
export const videoStore = new GlobalState(videoProxy);
export const kioskStore = new GlobalState(kioskProxy);

